import React, { useEffect, useMemo, useState, useRef } from "react";
import toast from "react-hot-toast";
import { ref, update } from "firebase/database";
import { database } from "../../../firebaseConfig";

const REQUIRED_COLUMNS = [
  "Client_Id",
  "Year",
  "Cloud_Name",
  "Disbursement_Phone_Number",
  "Amount",
  "Current_Client_Phone_Number",
  "Current_Client_Identification_Number",
  "Mode",
];

const parseCsvText = (text) => {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length === 0) return [];
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cells = line.split(",").map((c) => c.trim());
    const row = {};
    headers.forEach((h, idx) => {
      row[h] = cells[idx] ?? "";
    });
    return row;
  });
};

const InfoCard = ({ title, value, description }) => (
  <div className="bg-white rounded-lg border shadow-sm p-4">
    <div className="text-xs text-gray-500">{title}</div>
    <div className="text-lg font-semibold text-gray-800">{value}</div>
    {description && <div className="text-xs text-gray-500 mt-1">{description}</div>}
  </div>
);

/**
 * Basic stats helpers
 */
const computeStats = (values) => {
  if (!values.length) {
    return {
      mean: 0,
      median: 0,
      stdDev: 0,
      zScore: 0,
      likelihood: 0,
    };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const sum = sorted.reduce((s, v) => s + v, 0);
  const mean = sum / n;
  const median =
    n % 2 === 1 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;

  const variance =
    n > 1 ? sorted.reduce((s, v) => s + (v - mean) ** 2, 0) / (n - 1) : 0;
  const stdDev = Math.sqrt(variance);

  return {
    mean,
    median,
    stdDev,
  };
};

const PaymentValidationModal = ({ open, onClose, payment, resolvePhoneNumber, companyName }) => {
  const [rawFile, setRawFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);

  const svgRef = useRef(null);
  const [viewWindow, setViewWindow] = useState({ start: 0, end: 1 }); // fraction [0,1] of time
  const [zoomY, setZoomY] = useState(1); // Y zoom factor
  const [isPanning, setIsPanning] = useState(false);
  const [panStartX, setPanStartX] = useState(null);
  const [panStartWindow, setPanStartWindow] = useState(null);

  const getClientId = (entry) => entry?.Client_Id || entry?.ClientId || entry?.ClientID || "";
  const getAmountValue = (entry) => Number(entry?.Amount ?? entry?.amount ?? 0);
  const effectiveCompanyName =
    payment?.CompanyName || payment?.companyName || companyName || "";

  // Fixed reference: always the payment amount we intend to send
  const referenceAmount = getAmountValue(payment);

  // Simple name similarity using token overlap + amount closeness
  const normalizeName = (name) => String(name || "").toLowerCase().trim();
  const nameSimilarity = (a, b) => {
    const na = normalizeName(a);
    const nb = normalizeName(b);
    if (!na || !nb) return 0;
    if (na === nb) return 1;
    if (na.includes(nb) || nb.includes(na)) return 0.8;
    const setA = new Set(na.split(/\s+/));
    const setB = new Set(nb.split(/\s+/));
    const intersection = [...setA].filter((x) => setB.has(x)).length;
    const union = new Set([...setA, ...setB]).size;
    if (union === 0) return 0;
    return intersection / union;
  };

  const bestNameCandidates = useMemo(() => {
    const paymentName =
      payment?.CloudName ||
      payment?.OtherName ||
      payment?.Payment_Name ||
      payment?.PaymentName ||
      "";

    if (!rows.length || !paymentName) return [];

    const scored = rows.map((r, idx) => {
      const rowName = r.Payment_Name || r.Cloud_Name || r.OtherName || "";
      const ns = nameSimilarity(rowName, paymentName);
      const amt = getAmountValue(r);
      const amtScore =
        referenceAmount > 0 && amt > 0 ? Math.max(0, 1 - Math.abs(amt - referenceAmount) / referenceAmount) : 0;
      const score = 0.6 * ns + 0.4 * amtScore;
      return {
        key: `${rowName || "row"}-${idx}`,
        name: rowName || "Unknown",
        likelihood: Math.max(0, Math.min(100, Math.round(score * 100))),
      };
    });

    return scored
      .filter((s) => s.likelihood > 0)
      .sort((a, b) => b.likelihood - a.likelihood)
      .slice(0, 3);
  }, [rows, payment, referenceAmount]);

  // Filtered just for the table / matches card (by Payment_Name / Cloud_Name / OtherName)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = (r.Payment_Name || r.Cloud_Name || r.OtherName || "").toLowerCase();
      return name.includes(q);
    });
  }, [rows, query]);

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  // The currently "focused" client: from clicked row if any, otherwise from the payment itself
  const effectiveClientId = getClientId(selectedRow) || getClientId(payment);

  // Build a proper time-series for the farmer using ALL rows for that Client_Id
  const buildDateParts = (entry) => {
    const y = Number(entry.Year);
    const m = Number(entry.Month);
    const d = Number(entry.Day);
    if (!y || !m || !d) return null;
    const date = new Date(y, m - 1, d);
    if (Number.isNaN(date.getTime())) return null;
    return {
      date,
      year: y,
      month: m,
      day: d,
    };
  };

  const farmerHistory = useMemo(() => {
    if (!effectiveClientId) return [];

    const dataset = rows.filter((r) => getClientId(r) === effectiveClientId);

    return dataset
      .map((r) => {
        const parts = buildDateParts(r);
        if (!parts) return null;
        return {
          ...parts, // date, year, month, day
          amount: getAmountValue(r),
        };
      })
      .filter((r) => r && r.amount > 0)
      .sort((a, b) => a.date - b.date); // time ascending
  }, [rows, effectiveClientId]);

  // Derive axis labeling strategy
  const xLabelFormatter = useMemo(() => {
    if (farmerHistory.length === 0) return () => "";

    const years = Array.from(new Set(farmerHistory.map((h) => h.year)));
    if (years.length > 1) {
      // 2+ years => show year only
      return (h) => String(h.year);
    }

    // Single year
    const months = Array.from(new Set(farmerHistory.map((h) => h.month)));
    if (months.length > 4) {
      // Many months in one year => year + month
      return (h) => `${h.year}-${String(h.month).padStart(2, "0")}`;
    }

    // Few points => full date
    return (h) =>
      `${h.year}-${String(h.month).padStart(2, "0")}-${String(h.day).padStart(2, "0")}`;
  }, [farmerHistory]);

  // Compute stats & likelihood for the third card
  const stats = useMemo(() => {
    const values = farmerHistory.map((h) => h.amount);
    const { mean, median, stdDev } = computeStats(values);
    let zScore = 0;
    if (stdDev > 0) {
      zScore = (referenceAmount - mean) / stdDev;
    }

    // Simple likelihood heuristic:
    // If ref is close to mean (|z| <= 0.5) → near 100%
    // If 0.5 < |z| <= 1.5 → 60–90%
    // If larger → quickly drop below 50%
    const absZ = Math.abs(zScore);
    let likelihood = 0;
    if (farmerHistory.length === 0 || referenceAmount <= 0) {
      likelihood = 0;
    } else if (absZ <= 0.5) {
      likelihood = 95;
    } else if (absZ <= 1.5) {
      likelihood = 80 - (absZ - 0.5) * 20; // from 80 down to 60
    } else if (absZ <= 3) {
      likelihood = 60 - (absZ - 1.5) * 30; // from 60 down to ~15
    } else {
      likelihood = 10;
    }

    likelihood = Math.max(0, Math.min(100, likelihood));
    return {
      mean,
      median,
      stdDev,
      zScore,
      likelihood,
    };
  }, [farmerHistory, referenceAmount]);

  useEffect(() => {
    setPage(1);
  }, [query, pageSize, filtered.length]);

  useEffect(() => {
    setSelectedRow(null);
    setMatchLoading(false);
  }, [payment]);

  // Reset zoom / view window when history changes
  useEffect(() => {
    setViewWindow({ start: 0, end: 1 });
    setZoomY(1);
  }, [farmerHistory.length, effectiveClientId]);

  const handleWheel = (e) => {
    e.preventDefault();
    if (!farmerHistory.length) return;

    // Zoom Y on wheel (scroll up = zoom in, scroll down = zoom out)
    const delta = Math.sign(e.deltaY);
    setZoomY((prev) => {
      const next = prev * (delta > 0 ? 0.9 : 1.1);
      return Math.max(0.5, Math.min(5, next));
    });
  };

  // Resolve farmer identifier by priority: explicit payment names first, then fallback to CSV row names
  const getFarmerKey = () =>
    payment?.OtherName ||
    payment?.otherName ||
    payment?.CloudName ||
    payment?.cloudName ||
    selectedRow?.Payment_Name ||
    selectedRow?.Cloud_Name ||
    "";

  const handleMatch = async () => {
    if (!selectedRow) {
      toast.error("Select a row first");
      return;
    }
    const farmerKey = getFarmerKey();
    if (!farmerKey) {
      toast.error("Missing farmer identifier");
      return;
    }
    if (!effectiveCompanyName) {
      toast.error("Company not available");
      return;
    }

    const phone = selectedRow.Current_Client_Phone_Number || "";
    const idNum = selectedRow.Current_Client_Identification_Number || "";
    const mode = selectedRow.Mode || "";
    const payload = {};
    if (phone) payload.PhoneNumber = phone;
    if (idNum) payload.IdentificationNumber = idNum;
    if (mode) payload.Mode = mode;
    if (!phone && !idNum && !mode) {
      toast.error("No fields to update");
      return;
    }

    setMatchLoading(true);
    const basePath = `Companies/${effectiveCompanyName}`;
    const farmerPaths = [
      `${basePath}/ValidatedFarmers/${farmerKey}`,
      `${basePath}/AIMilkValidatedFarmers/${farmerKey}`,
      `${basePath}/NewValidatedFarmers/${farmerKey}`,
    ];

    try {
      const results = await Promise.allSettled(
        farmerPaths.map((p) => update(ref(database, p), payload))
      );

      const failures = results.filter((r) => r.status === "rejected");
      if (failures.length === results.length) {
        const firstError = failures[0]?.reason || failures[0];
        const msg = typeof firstError === "string" ? firstError : firstError?.message;
        throw msg ? new Error(msg) : new Error("All updates failed");
      }

      if (failures.length > 0) {
        toast.error(`${failures.length} of ${results.length} updates failed`);
      } else {
        toast.success("Farmer updated from CSV row");
      }
    } catch (err) {
      console.error("Match update error", err);
      toast.error("Failed to update farmer");
    } finally {
      setMatchLoading(false);
    }
  };

  const handleMouseDown = (e) => {
    if (!farmerHistory.length) return;
    setIsPanning(true);
    setPanStartX(e.clientX);
    setPanStartWindow(viewWindow);
  };

  const handleMouseMove = (e) => {
    if (!isPanning || !farmerHistory.length || !panStartWindow) return;
    const svg = svgRef.current;
    if (!svg) return;

    const bbox = svg.getBoundingClientRect();
    const dx = e.clientX - panStartX;
    const fraction = dx / bbox.width;
    const range = panStartWindow.end - panStartWindow.start;
    let newStart = panStartWindow.start - fraction * range;
    let newEnd = panStartWindow.end - fraction * range;

    // Clamp within [0,1]
    const width = newEnd - newStart;
    if (newStart < 0) {
      newStart = 0;
      newEnd = width;
    }
    if (newEnd > 1) {
      newEnd = 1;
      newStart = 1 - width;
    }
    setViewWindow({ start: newStart, end: newEnd });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    setPanStartWindow(null);
  };

  const handleDoubleClick = () => {
    // Reset zoom/pan
    setViewWindow({ start: 0, end: 1 });
    setZoomY(1);
  };

  if (!open || !payment) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-validation-title"
    >
      {/* Modal container: constrained height + internal scroll */}
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <div className="text-sm text-gray-500" id="payment-validation-title">
              Payment validation
            </div>
            <div className="text-lg font-semibold text-gray-800">
              {payment.CloudName || payment.OtherName || payment.Payment_Name || payment.Id}
            </div>
            <div className="text-xs text-gray-500">
              Amount: {payment.Amount} · Mode: {payment.Mode || "—"} · Phone:{" "}
              {resolvePhoneNumber ? resolvePhoneNumber(payment, {}) : "—"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setRawFile(file);
                setLoading(true);
                const reader = new FileReader();
                reader.onload = (ev) => {
                  const text = ev.target?.result || "";
                  const parsed = typeof text === "string" ? parseCsvText(text) : [];
                  setRows(parsed);
                  setLoading(false);
                };
                reader.onerror = () => {
                  setLoading(false);
                  toast.error("Failed to read file");
                };
                reader.readAsText(file);
              }}
              className="text-xs"
            />
            <button onClick={onClose} className="text-sm px-3 py-1 rounded border" aria-label="Close modal">
              Close
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Info cards + filter */}
          <div className="border-b px-4 py-3 flex flex-col gap-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <InfoCard
                title="Selected payment"
                value={payment.CloudName || payment.OtherName || payment.Id || "—"}
              />
              <InfoCard
                title="Uploaded file"
                value={rawFile ? rawFile.name : "No file yet"}
                description="Attach farmer_data.csv"
              />
              <InfoCard
                title="Matches"
                value={`${filtered.length} / ${rows.length}`}
                description="Filtered by name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <InfoCard
                title="Selected Client_Id"
                value={effectiveClientId || "—"}
                description="Click a row to pick"
              />
              <InfoCard
                title="Reference amount"
                value={referenceAmount || 0}
                description="Drawn as red line"
              />
              <div className="bg-white rounded-lg border shadow-sm p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-500">Possible matches</div>
                    <div className="text-sm text-gray-800">Top 3 by name & amount</div>
                  </div>
                  <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" aria-label="Match suggestions spinner" />
                </div>
                <div className="mt-3 space-y-2 text-sm text-gray-700">
                  {bestNameCandidates.length === 0 ? (
                    <div className="text-gray-500">No suggestions yet</div>
                  ) : (
                    bestNameCandidates.map((c) => (
                      <div key={c.key} className="flex items-center justify-between">
                        <span className="truncate">{c.name}</span>
                        <span className="text-cyan-700 font-semibold">{c.likelihood}%</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="Filter by name e.g. alice"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="border rounded px-3 py-1 text-sm"
              />
              {loading && <span className="text-xs text-gray-500">Loading…</span>}
            </div>
          </div>

          {/* Main content */}
          <div className="p-4">
            <div className="grid grid-cols-1 gap-4">
              {/* Records */}
              <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Records</div>
                    <div className="text-xs text-gray-500">Showing farmer payments for validation</div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {filtered.length} total · page {page} of {totalPages}
                  </div>
                </div>

                <div className="overflow-auto max-h-[340px]">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {REQUIRED_COLUMNS.map((col) => (
                          <th
                            key={col}
                            className="px-3 py-2 text-left text-gray-600 font-semibold border-b"
                          >
                            {col.replace(/_/g, " ")}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length === 0 ? (
                        <tr>
                          <td
                            colSpan={REQUIRED_COLUMNS.length}
                            className="px-3 py-4 text-center text-gray-500"
                          >
                            {rows.length === 0
                              ? "Upload farmer_data.csv to start"
                              : "No matches for this filter"}
                          </td>
                        </tr>
                      ) : (
                        paginated.map((row, idx) => {
                          const rowClientId = getClientId(row);
                          const isSelected = effectiveClientId && rowClientId === effectiveClientId;
                          const rowClass = isSelected ? "bg-cyan-50" : "odd:bg-white even:bg-gray-50";
                          return (
                            <tr
                              key={idx}
                              className={`${rowClass} cursor-pointer`}
                              onClick={() => setSelectedRow(row)}
                            >
                              {REQUIRED_COLUMNS.map((col) => (
                                <td
                                  key={col}
                                  className="px-3 py-2 border-b text-gray-700 whitespace-nowrap"
                                >
                                  {row[col] ?? "—"}
                                </td>
                              ))}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-end gap-3 px-4 py-3 border-t">
                  <div className="text-xs text-gray-500 mr-auto">
                    {filtered.length > 0
                      ? `Showing ${(page - 1) * pageSize + 1} - ${Math.min(
                          filtered.length,
                          (page - 1) * pageSize + paginated.length
                        )} of ${filtered.length}`
                      : "No records"}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span>Rows per page</span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      {[10, 20, 50].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="px-2 py-1 rounded border text-xs disabled:opacity-50"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Prev
                  </button>
                  <button
                    className="px-2 py-1 rounded border text-xs disabled:opacity-50"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>

              {/* Payment trend with pan/zoom */}
              <div className="bg-white rounded-lg border shadow-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Payment trend</div>
                    <div className="text-xs text-gray-500">
                      Client_ID: {effectiveClientId || "—"}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                      Scroll to zoom (Y), drag horizontally to pan, double‑click to reset.
                    </div>
                  </div>
                  <div className="text-xs text-rose-600 font-semibold">
                    Reference: {referenceAmount || 0}
                  </div>
                </div>

                {farmerHistory.length === 0 ? (
                  <div className="text-sm text-gray-500">
                    No history for this farmer.
                  </div>
                ) : (
                  <div className="h-56 relative">
                    <svg
                      ref={svgRef}
                      viewBox="0 0 360 200"
                      className="w-full h-full border border-gray-100 rounded"
                      onWheel={handleWheel}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onDoubleClick={handleDoubleClick}
                    >
                      {(() => {
                        const n = farmerHistory.length;
                        const values = farmerHistory.map((f) => f.amount);
                        const target = referenceAmount;
                        const maxVal = Math.max(...values, target || 0, 1) * zoomY;

                        // X-axis mapping with pan
                        const { start, end } = viewWindow;
                        const visibleStartIdx = Math.floor(start * (n - 1));
                        const visibleEndIdx = Math.ceil(end * (n - 1));

                        const visible = farmerHistory.slice(
                          Math.max(0, visibleStartIdx),
                          Math.min(n, visibleEndIdx + 1)
                        );

                        if (!visible.length) return null;

                        const xPaddingLeft = 40; // room for Y labels
                        const xPaddingRight = 10;
                        const yPaddingTop = 10;
                        const yPaddingBottom = 30; // room for X labels

                        const innerWidth = 360 - xPaddingLeft - xPaddingRight;
                        const innerHeight = 200 - yPaddingTop - yPaddingBottom;

                        const step =
                          visible.length > 1 ? innerWidth / (visible.length - 1) : 0;

                        const mapX = (idxVisible) =>
                          xPaddingLeft + step * idxVisible;

                        const mapY = (amount) =>
                          yPaddingTop + innerHeight - (amount / maxVal) * innerHeight;

                        // Polyline points
                        const points = visible
                          .map((f, idx) => `${mapX(idx)},${mapY(f.amount)}`)
                          .join(" ");

                        // Reference line
                        const targetY = mapY(target);

                        // Y-axis ticks (3–5 ticks)
                        const ticks = [];
                        const tickCount = 4;
                        for (let i = 0; i <= tickCount; i += 1) {
                          const ratio = i / tickCount;
                          const value = maxVal * ratio;
                          const y = mapY(value);
                          ticks.push({ value, y });
                        }

                        return (
                          <>
                            {/* Y grid + labels */}
                            {ticks.map((t, idx) => (
                              <g key={idx}>
                                <line
                                  x1={xPaddingLeft}
                                  x2={360 - xPaddingRight}
                                  y1={t.y}
                                  y2={t.y}
                                  stroke="#e5e7eb"
                                  strokeWidth="0.5"
                                />
                                <text
                                  x={xPaddingLeft - 4}
                                  y={t.y + 3}
                                  textAnchor="end"
                                  fontSize="9"
                                  fill="#6b7280"
                                >
                                  {Math.round(t.value).toLocaleString()}
                                </text>
                              </g>
                            ))}

                            {/* X-axis line */}
                            <line
                              x1={xPaddingLeft}
                              x2={360 - xPaddingRight}
                              y1={yPaddingTop + innerHeight}
                              y2={yPaddingTop + innerHeight}
                              stroke="#9ca3af"
                              strokeWidth="0.5"
                            />

                            {/* Reference line */}
                            <line
                              x1={xPaddingLeft}
                              x2={360 - xPaddingRight}
                              y1={targetY}
                              y2={targetY}
                              stroke="red"
                              strokeDasharray="4 4"
                            />

                            {/* Data polyline */}
                            <polyline
                              points={points}
                              fill="none"
                              stroke="#0ea5e9"
                              strokeWidth="2"
                            />

                            {/* Points */}
                            {visible.map((f, idx) => (
                              <circle
                                key={idx}
                                cx={mapX(idx)}
                                cy={mapY(f.amount)}
                                r="3"
                                fill="#0ea5e9"
                              />
                            ))}

                            {/* X labels (at visible indices) */}
                            {visible.map((f, idx) => (
                              <text
                                key={idx}
                                x={mapX(idx)}
                                y={yPaddingTop + innerHeight + 14}
                                textAnchor="middle"
                                fontSize="9"
                                fill="#6b7280"
                              >
                                {xLabelFormatter(f)}
                              </text>
                            ))}
                          </>
                        );
                      })()}
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* Third card – likelihood pie + stats */}
            <div className="mt-4 bg-white rounded-lg border shadow-sm p-4 text-sm text-gray-700">
              <div className="flex items-start justify-between gap-6 flex-col md:flex-row">
                {/* Pie chart */}
                <div className="flex-1 flex items-center justify-center">
                  {farmerHistory.length === 0 || referenceAmount <= 0 ? (
                    <div className="text-sm text-gray-500">
                      Not enough data to estimate likelihood.
                    </div>
                  ) : (
                    <svg viewBox="0 0 200 200" className="w-40 h-40">
                      {(() => {
                        const like = stats.likelihood;
                        const rad = (like / 100) * 2 * Math.PI;
                        const cx = 100;
                        const cy = 100;
                        const r = 70;

                        const x = cx + r * Math.sin(rad);
                        const y = cy - r * Math.cos(rad);
                        const largeArc = like > 50 ? 1 : 0;

                        const dLike = `
                          M ${cx} ${cy - r}
                          A ${r} ${r} 0 ${largeArc} 1 ${x} ${y}
                          L ${cx} ${cy}
                          Z
                        `;

                        return (
                          <>
                            {/* Uncertain background */}
                            <circle cx={cx} cy={cy} r={r} fill="#e5e7eb" />
                            {/* Likely segment */}
                            <path d={dLike} fill="#10b981" />
                            <text
                              x={cx}
                              y={cy}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fontSize="16"
                              fontWeight="600"
                              fill="#111827"
                            >
                              {like.toFixed(0)}%
                            </text>
                            <text
                              x={cx}
                              y={cy + 20}
                              textAnchor="middle"
                              fontSize="10"
                              fill="#6b7280"
                            >
                              Likely match
                            </text>
                          </>
                        );
                      })()}
                    </svg>
                  )}
                </div>

                {/* Stats summary */}
                <div className="flex-[1.5] space-y-1 text-xs md:text-sm">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="font-semibold text-gray-800">
                      Likelihood this is the intended farmer
                    </div>
                    <button
                      type="button"
                      onClick={handleMatch}
                      disabled={matchLoading || !selectedRow}
                      className="px-3 py-1 rounded bg-emerald-600 text-white text-xs disabled:opacity-50"
                    >
                      {matchLoading ? "Matching…" : "Match"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-y-1 gap-x-4">
                    <div className="text-gray-500">Reference amount</div>
                    <div className="text-gray-900 font-medium">
                      {referenceAmount.toLocaleString()}
                    </div>

                    <div className="text-gray-500">Mean payment</div>
                    <div className="text-gray-900">
                      {stats.mean.toFixed(2).toLocaleString()}
                    </div>

                    <div className="text-gray-500">Median payment</div>
                    <div className="text-gray-900">
                      {stats.median.toFixed(2).toLocaleString()}
                    </div>

                    <div className="text-gray-500">Std deviation</div>
                    <div className="text-gray-900">
                      {stats.stdDev.toFixed(2).toLocaleString()}
                    </div>

                    <div className="text-gray-500">Z-score</div>
                    <div className="text-gray-900">
                      {Number.isFinite(stats.zScore)
                        ? stats.zScore.toFixed(2)
                        : "—"}
                    </div>

                    <div className="text-gray-500">Match likelihood</div>
                    <div className="text-gray-900 font-semibold">
                      {stats.likelihood.toFixed(0)}%
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-gray-500">
                    This is a heuristic score based on how close the reference amount is to
                    this farmer&#39;s historical payments (mean and variability). Use it as
                    guidance, not as a final decision.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentValidationModal;
